<template>
  <!-- Who at Screenkode looks after a ticket: "Assign to me" while nobody
       does — or who does — and a menu that gives it to someone else, with what
       each already has open, or to nobody. Staff only (the admin); someone
       given a ticket by a colleague is emailed (PUT /ticket/:id/assignee).
       The same component lives in the admin and in both organization apps. -->
  <div ref="root" class="ta" :class="{ 'is-assigned': assignee }" @keydown.esc.stop="closeMenu">
    <button
      v-if="!assignee"
      type="button"
      class="ta-btn ta-btn--take"
      :disabled="busy"
      title="Assign the ticket to yourself"
      @click="assignTo(myId)"
    >
      <span v-if="busy" class="spinner-border spinner-border-sm" aria-hidden="true"></span>
      <i v-else class="pi pi-user-plus" aria-hidden="true"></i>
      <span class="ta-btn__label">Assign to me</span>
    </button>
    <button
      type="button"
      class="ta-btn ta-toggle"
      :class="{ 'is-open': open }"
      :disabled="busy"
      aria-haspopup="menu"
      :aria-expanded="open ? 'true' : 'false'"
      :aria-label="assignee ? `${assignedLine}. Change who looks after it` : 'Assign to someone else'"
      :title="assignee ? assignedLine : 'Assign to someone else'"
      @click="toggleMenu"
    >
      <template v-if="assignee">
        <span class="ta-avatar" :class="avatarColorClass(assigneeName)" aria-hidden="true">{{ initialsOf(assigneeName) }}</span>
        <span class="ta-btn__label">{{ assigneeIsMe ? 'You' : assigneeName }}</span>
      </template>
      <i class="pi pi-chevron-down ta-caret" aria-hidden="true"></i>
    </button>

    <div v-if="open" class="ta-menu" role="menu" aria-label="Assign to">
      <p class="ta-menu__title">Assign to</p>
      <div v-if="!staff.length" class="ta-menu__state" role="status">
        <span class="spinner-border spinner-border-sm" aria-hidden="true"></span>
        <span>Loading…</span>
      </div>
      <button
        v-for="person in staff"
        :key="person.id"
        type="button"
        role="menuitemradio"
        class="ta-item"
        :class="{ 'is-current': assignee && assignee.id === person.id }"
        :aria-checked="assignee && assignee.id === person.id ? 'true' : 'false'"
        @click="assignTo(person.id)"
      >
        <span class="ta-avatar ta-avatar--lg" :class="avatarColorClass(nameOf(person))" aria-hidden="true">{{ initialsOf(nameOf(person)) }}</span>
        <span class="ta-item__who">
          <span class="ta-item__name">{{ nameOf(person) }}<span v-if="person.isMe" class="ta-item__you"> (you)</span></span>
          <span class="ta-item__meta">{{ loadOf(person) }}</span>
        </span>
        <i v-if="assignee && assignee.id === person.id" class="pi pi-check ta-item__check" aria-hidden="true"></i>
      </button>
      <template v-if="assignee">
        <div class="ta-menu__divider" role="separator"></div>
        <button type="button" role="menuitem" class="ta-item ta-item--clear" @click="assignTo(null)">
          <span class="ta-item__clear-icon" aria-hidden="true"><i class="pi pi-user-minus"></i></span>
          <span class="ta-item__who"><span class="ta-item__name">Unassign</span></span>
        </button>
      </template>
    </div>
  </div>
</template>
<script>
import Ticket from '@/services/ticket';
import { ASSIGN_TICKET } from '@/store/action_types';
import { TOAST_GLOBAL_CONFIG } from '@/utils/const';
import { avatarColorClass, initialsOf, messageTimeLabel, personName } from '@/utils/ticket';

// The staff list changes rarely: asked for again when the menu opens and what
// is known is older than this.
const STAFF_FRESH_MS = 30 * 1000;

export default {
  name: 'TicketAssignee',
  props: {
    ticket: {
      type: Object,
      required: true,
    },
  },
  emits: ['assigned'],
  data() {
    return {
      open: false,
      busy: false,
      staff: [],
    };
  },
  computed: {
    myId() {
      return this.$store.getters.account.data?.id || null;
    },
    assignee() {
      const person = this.ticket.assignedTo;
      return person && person.id ? person : null;
    },
    assigneeName() {
      return personName(this.assignee, 'Someone');
    },
    assigneeIsMe() {
      return Boolean(this.assignee && this.assignee.id === this.myId);
    },
    // "Assigned to Sara Katz by you · 3:12 PM", "Assigned to you · Yesterday".
    assignedLine() {
      if (!this.assignee) return '';
      const to = this.assigneeIsMe ? 'you' : this.assigneeName;
      const by = this.ticket.assignedBy;
      let byLabel = '';
      if (by && by.id && by.id !== this.assignee.id) byLabel = by.id === this.myId ? 'you' : personName(by, '');
      const when = messageTimeLabel(this.ticket.assignedAt);
      return `Assigned to ${to}${byLabel ? ` by ${byLabel}` : ''}${when ? ` · ${when}` : ''}`;
    },
  },
  watch: {
    // Another ticket: the menu closes.
    'ticket.id'() {
      this.closeMenu();
    },
  },
  methods: {
    avatarColorClass,
    initialsOf,
    nameOf(person) {
      return personName(person, 'Someone');
    },
    // "3 open tickets", "Nothing open".
    loadOf(person) {
      const count = Number(person.openTickets) || 0;
      return count ? `${count} open ${count === 1 ? 'ticket' : 'tickets'}` : 'Nothing open';
    },
    async loadStaff() {
      const result = await Ticket.getStaff().catch(() => null);
      if (this.isGone) return;
      if (result && result.data?.status && Array.isArray(result.data.data)) {
        this.staff = result.data.data;
        this.staffLoadedAt = Date.now();
      }
    },
    toggleMenu() {
      if (this.open) this.closeMenu();
      else this.openMenu();
    },
    openMenu() {
      this.open = true;
      if (!this.staff.length || Date.now() - this.staffLoadedAt > STAFF_FRESH_MS) this.loadStaff();
      document.addEventListener('pointerdown', this.onPointer, true);
    },
    closeMenu() {
      this.open = false;
      document.removeEventListener('pointerdown', this.onPointer, true);
    },
    onPointer(event) {
      if (this.$refs.root && !this.$refs.root.contains(event.target)) this.closeMenu();
    },
    // To someone at Screenkode — the viewer, or a colleague, who is emailed —
    // or, with null, to nobody.
    async assignTo(personId) {
      this.closeMenu();
      if (this.busy) return;
      if ((this.assignee ? this.assignee.id : null) === (personId || null)) return;
      this.busy = true;
      let updated = null;
      try {
        updated = await this.$store.dispatch(ASSIGN_TICKET, { id: this.ticket.id, assigneeId: personId || null });
      } finally {
        this.busy = false;
      }
      if (!updated || this.isGone) return;
      const person = updated.assignedTo;
      let said = 'Ticket unassigned.';
      if (person && person.id === this.myId) said = 'Assigned to you.';
      else if (person) said = `Assigned to ${personName(person, 'them')}. They've been emailed.`;
      this.$toast.success(said, { ...TOAST_GLOBAL_CONFIG, duration: 2500 });
      this.$emit('assigned', updated);
      this.staffLoadedAt = 0;
    },
  },
  created() {
    this.isGone = false;
    this.staffLoadedAt = 0;
  },
  beforeUnmount() {
    this.isGone = true;
    document.removeEventListener('pointerdown', this.onPointer, true);
  },
};
</script>
<style scoped>
.ta {
  position: relative;
  display: inline-flex;
  flex: none;
}

.ta-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 32px;
  padding: 0 12px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md, 8px);
  background: var(--color-bg-canvas);
  color: var(--color-text-secondary);
  font-size: 0.78rem;
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color .15s ease, border-color .15s ease, color .15s ease;
}

.ta-btn:hover:not(:disabled) {
  border-color: var(--color-primary-border);
  background: var(--color-primary-soft);
  color: var(--color-primary-hover);
}

.ta-btn:focus-visible {
  outline: none;
  box-shadow: var(--shadow-focus);
}

.ta-btn:disabled {
  opacity: .6;
  cursor: not-allowed;
}

.ta-btn .pi {
  margin: 0;
  font-size: 0.78rem;
}

/* Nobody has it: "Assign to me" and the caret, as one primary-tinted button. */
.ta:not(.is-assigned) .ta-btn {
  border-color: var(--color-primary-border);
  background: var(--color-primary-soft);
  color: var(--color-primary-hover);
  font-weight: 600;
}

.ta:not(.is-assigned) .ta-btn:hover:not(:disabled) {
  background: #E2E0FC;
}

.ta:not(.is-assigned) .ta-btn--take {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.ta:not(.is-assigned) .ta-toggle {
  width: 30px;
  margin-left: -1px;
  padding: 0;
  justify-content: center;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}

.ta.is-assigned .ta-toggle {
  padding-left: 6px;
}

.ta-caret {
  font-size: 0.6rem !important;
  transition: transform .15s ease;
}

.ta-toggle.is-open .ta-caret {
  transform: rotate(180deg);
}

.ta-avatar {
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  font-size: 0.58rem;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.ta-avatar--lg {
  width: 28px;
  height: 28px;
  font-size: 0.66rem;
}

.ta-menu {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 60;
  width: 280px;
  max-width: calc(100vw - 32px);
  max-height: 340px;
  padding: 6px;
  overflow-y: auto;
  border: 1px solid var(--color-border);
  border-radius: 10px;
  background: var(--color-bg-canvas);
  box-shadow: 0 12px 28px rgba(24, 24, 27, 0.14);
  text-align: left;
  animation: ta-menu-in .14s ease-out;
}

@keyframes ta-menu-in {
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: none; }
}

.ta-menu__title {
  margin: 4px 8px 6px;
  color: var(--color-text-muted);
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.ta-menu__state {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  color: var(--color-text-muted);
  font-size: 0.78rem;
}

.ta-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 7px 8px;
  border: 0;
  border-radius: 8px;
  background: none;
  text-align: left;
  cursor: pointer;
  transition: background-color .12s ease;
}

.ta-item:hover,
.ta-item:focus-visible {
  background: var(--color-bg-muted);
  outline: none;
}

.ta-item.is-current {
  background: var(--color-primary-soft);
}

.ta-item__who {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
}

.ta-item__name {
  overflow: hidden;
  color: var(--color-text-primary);
  font-size: 0.82rem;
  font-weight: 500;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ta-item__you {
  color: var(--color-text-muted);
  font-weight: 400;
}

.ta-item__meta {
  color: var(--color-text-muted);
  font-size: 0.72rem;
}

.ta-item__check {
  flex: none;
  color: var(--color-primary);
  font-size: 0.75rem;
}

.ta-menu__divider {
  height: 1px;
  margin: 6px 4px;
  background: var(--color-divider);
}

.ta-item__clear-icon {
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--color-bg-muted);
  color: var(--color-text-secondary);
  font-size: 0.8rem;
}

/* A phone: the button keeps its icon; who has it is the avatar. */
@media (max-width: 575.98px) {
  .ta-btn__label {
    display: none;
  }

  .ta-btn--take {
    width: 34px;
    padding: 0;
    justify-content: center;
  }
}
</style>
